/*
 * utils.pdfmake.ts
 *
 * Define common pdfmake's TDocumentationDefinitions
 *
 * Param1: [Filename] - Text;
 * Param2: [Subject ] - Text;
 * Param3: [Keywords] - Text;
 * Param4: [NameForm] - Text, name of document / form template;
 *
 */
import { type TDocumentDefinitions } from 'pdfmake/interfaces';
import { msImageBlankBase64PNG } from 'src/config/config.img.base64';
import { mcUtils } from 'src/utils/utils';
//
const msURLLogoeLC = process.env.eLC_APP_URL + '/logo/logo_eLC.png';
const msURLLogoIndeco = process.env.eLC_APP_URL + '/logo/logo_indeco.png';
//
export class mcUtilsPdfmake {
  public cpifGetTDocDefinitions(
    psPDFFilename,
    psPDFSubject,
    psPDFKeywords,
    psPDFNameForm,
    pbPDFPasswordProtect = false,
  ) {
    const moPDFDocDefinition: TDocumentDefinitions = {
      // Document Meta Data (Visible in document properties)
      info: {
        title: `${psPDFFilename}`,
        author: 'eLC Pte Ltd',
        subject: `${psPDFSubject}`,
        keywords: `${psPDFKeywords}`,
        // creator: 'eLC Pte Ltd',
        creator: 'eLC-eSystem-App',
        producer: 'pdfmake-0.2.18',
        creationDate: new Date(),
        modDate: new Date(),
      },
      // Document Compression (May substantially reduce size of PDF file!)
      compress: true,
      ...(pbPDFPasswordProtect
        ? {
            // Document Version of PDF (Required for and specifies encryption type!)
            version: '1.7',
            // Document User Password (Required to activate encryption!)
            userPassword: '',
            // Document Owner Password (Required to activate permissions!)
            ownerPassword: 'eLCPteLtd',
            // Document Permissions
            permissions: {
              modifying: false,
              copying: false,
              annotating: false,
              fillingForms: false,
              contentAccessibility: false,
              documentAssembly: false,
              printing: 'highResolution',
              // exttractingPages: false,
            },
          }
        : {}),
      // Document Page Settings (Affect all pages!)
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [20, 60, 20, 40], // left, top, right, bottom
      // Background Layer (Prints on all pages!)
      // background: {
      //   // Document Border
      //   canvas: [
      //     {
      //       type: 'rect',
      //       x: 36,
      //       y: 36,
      //       w: 540,
      //       h: 702,
      //       r: 3,
      //       lineWidth: 1,
      //       lineColor: '#212121',
      //     },
      //   ],
      // },
      // Watermark (Prints on all pages!)
      watermark: {
        text: 'INDeLCO',
        opacity: 0.05,
        angle: 0,
        font: 'Roboto',
        fontSize: 72,
        color: '#6d4c41',
        bold: true,
        italics: false,
      },
      // Document Default Styles (Takes precendence until locally overwritten!)
      defaultStyle: {
        font: 'Roboto',
        fontSize: 12,
        // fontFeatures: undefined,
        lineHeight: 1,
        bold: false,
        italics: false,
        alignment: 'left',
        color: '#404040',
        // background: undefined,
        // margin: [0, 0, 0, 0], // left, top, right, bottom
      },
      // Document Custom Styles (Optionally applied locally!)
      styles: {
        textLeft: {
          alignment: 'left',
        },
        textCenter: {
          alignment: 'center',
        },
        textRight: {
          alignment: 'right',
        },
        textEndOfTable: {
          font: 'Roboto',
          fontSize: 12,
          bold: true,
          italics: false,
          alignment: 'left',
          color: '#101010',
          margin: [20, 5, 0, 15],
        },
        textEndOfReport: {
          font: 'Roboto',
          fontSize: 8,
          bold: false,
          italics: false,
          alignment: 'left',
          color: '#101010',
          margin: [0, 5, 0, 5],
        },
        textEndorsedBy: {
          font: 'Roboto',
          fontSize: 12,
          bold: true,
          italics: false,
          alignment: 'left',
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        textEndorsedDT: {
          font: 'Roboto',
          fontSize: 6,
          bold: false,
          italics: false,
          alignment: 'center',
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        textEndorsedCT: {
          font: 'Roboto',
          fontSize: 12,
          bold: true,
          italics: false,
          alignment: 'center',
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        titleDoc: {
          font: 'Roboto',
          fontSize: 16,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 5],
        },
        title1Page: {
          font: 'Roboto',
          fontSize: 14,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        title2Page: {
          font: 'Roboto',
          fontSize: 13,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 5, 0, 5],
        },
        title3Page: {
          font: 'Roboto',
          fontSize: 12,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 5, 0, 5],
        },
        title4Page: {
          font: 'Roboto',
          fontSize: 12,
          bold: true,
          italics: false,
          alignment: 'left',
          color: '#101010',
          margin: [0, 15, 0, 0],
        },
        tableMarginTopBot10: {
          margin: [0, 10, 0, 10],
        },
        tableHeader1: {
          font: 'Roboto',
          fontSize: 10,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableHeader2: {
          font: 'Roboto',
          fontSize: 9,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableHeader3: {
          font: 'Roboto',
          fontSize: 8,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableHeader4: {
          font: 'Roboto',
          fontSize: 7,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableHeader5: {
          font: 'Roboto',
          fontSize: 6,
          bold: true,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableCell1: {
          font: 'Roboto',
          fontSize: 10,
          bold: false,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableCell2: {
          font: 'Roboto',
          fontSize: 9,
          bold: false,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableCell3: {
          font: 'Roboto',
          fontSize: 8,
          bold: false,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableCell4: {
          font: 'Roboto',
          fontSize: 7,
          bold: false,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
        tableCell5: {
          font: 'Roboto',
          fontSize: 6,
          bold: false,
          italics: false,
          color: '#101010',
          margin: [0, 0, 0, 0],
        },
      },
      // IMAGES
      images: {
        logoeLC: {
          url: `${msURLLogoeLC}`,
        },
        logoIndeco: {
          url: `${msURLLogoIndeco}`,
        },
        pngBlank: `${msImageBlankBase64PNG}`,
      },
      // HEADER
      header: {
        id: 'header',
        columns: [
          // STACK 1
          {
            stack: [
              {
                image: 'logoIndeco',
                // image: 'logoeLC',
                margin: [20, 20, 0, 0],
                width: 100,
                height: 25,
              },
              // {
              //   text: 'header-left',
              //   font: 'Roboto',
              //   fontSize: 7.5,
              //   bold: true,
              //   italics: false,
              //   alignment: 'left',
              //   color: '#404040',
              //   margin: [10, 0, 0, 0], // left, top, right, bottom
              // },
            ],
            style: 'textLeft',
            width: '*',
          },
          // STACK 2
          {
            stack: [
              {
                text: `${psPDFFilename}`,
                font: 'Roboto',
                fontSize: 8,
                bold: true,
                italics: false,
                alignment: 'center',
                color: '#404040',
                margin: [0, 15, 0, 0], // left, top, right, bottom
              },
            ],
            style: 'textCenter',
            width: '*',
          },
          // STACK 3
          {
            stack: [
              {
                // image: 'logoIndeco',
                image: 'logoeLC',
                margin: [0, 20, 10, 0],
                width: 100,
                height: 25,
              },
              // {
              //   text: 'header-right',
              //   font: 'Roboto',
              //   fontSize: 7.5,
              //   bold: true,
              //   italics: false,
              //   alignment: 'right',
              //   color: '#404040',
              //   margin: [0, 0, 10, 00], // left, top, right, bottom
              // },
            ],
            style: 'textRight',
            width: '*',
          },
        ],
      },
      // FOOTER
      footer: function (currentPage, pageCount) {
        return {
          id: 'footer',
          columns: [
            // STACK 1
            {
              stack: [
                {
                  text: `Form ${psPDFNameForm} © ${new Date().getFullYear()} - eLC Pte Ltd.`,
                  font: 'Roboto',
                  fontSize: 8,
                  normal: true,
                  bold: true,
                  italics: false,
                  bolditalics: false,
                  alignment: 'left',
                  color: '#404040',
                  margin: [10, 10, 0, 0], // left, top, right, bottom
                },
              ],
              style: 'textLeft',
              width: '*',
            },
            // STACK 2
            {
              stack: [
                {
                  text: `Generated: ${mcUtils.cpsoQuasarDate.cpif2YYYYMMDDHHMMSS(new Date().toISOString())}`,
                  font: 'Roboto',
                  fontSize: 8,
                  normal: true,
                  bold: true,
                  italics: false,
                  bolditalics: false,
                  alignment: 'center',
                  color: '#404040',
                  margin: [0, 10, 0, 0], // left, top, right, bottom
                },
              ],
              style: 'textCenter',
              width: '*',
            },
            // STACK 3
            {
              stack: [
                {
                  text: 'PAGE ' + currentPage.toString() + ' of ' + pageCount.toString(),
                  font: 'Roboto',
                  fontSize: 8,
                  normal: true,
                  bold: true,
                  italics: false,
                  bolditalics: false,
                  alignment: 'right',
                  color: '#404040',
                  margin: [0, 10, 10, 0], // left, top, right, bottom
                },
              ],
              style: 'textRight',
              width: '*',
            },
          ],
        };
      },
      // CONTENT
      content: [],
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      pageBreakBefore: function (poCurrentNode, paFollowingNodesOnPage, paNodesOnNextPage, paPreviousNodesOnPage) {
        // Prevent page break if the title is the only content on the current page
        // and the following content would be pushed to the next page.
        // NOTE: Add <id: 'titleText'> attribute to the node!!!
        if (poCurrentNode.id) {
          const lsCurrentNodeId = poCurrentNode.id.toString() || ' ';
          if (lsCurrentNodeId.startsWith('titleText')) {
            //
            // console.log('poCurrentNode.text=<', poCurrentNode.text, '>!');
            // console.log(
            //   'poCurrentNode.startPostion.top/pageInnerHeight=<',
            //   poCurrentNode.startPosition.top,
            //   '/',
            //   poCurrentNode.startPosition.pageInnerHeight,
            //   '=',
            //   poCurrentNode.startPosition.top / poCurrentNode.startPosition.pageInnerHeight,
            //   '>!',
            // );
            //
            if (poCurrentNode.startPosition.top / poCurrentNode.startPosition.pageInnerHeight > 0.9) {
              return true;
            }
          }
        }
        //
        return false;
      },
    };
    //
    return moPDFDocDefinition;
  }
}
//#end#
